import type { FC, ReactNode } from 'react'
import type { LoginRole, Viewer } from '@/types'

import Link from 'next/link'
import { motion } from 'framer-motion'
import { ArrowRight, Check } from 'lucide-react'

interface HeroProps {
    viewer: Viewer | null
    onOpenLogin: (role: LoginRole, signUp?: boolean) => void
}

const LINES = [
    ["All-in-One", "Campus", "Platform"],
    ["Powering", "Engineering", "Growth"]
]

const reveal = (delay: number) => ({
    initial: { opacity: 0, y: 24, filter: 'blur(8px)' },
    animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
    transition: { delay, duration: 0.75, ease: [0.22, 1, 0.36, 1] as const }
})

export const HeroBackdrop: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='pointer-events-none absolute inset-0 overflow-hidden'>
            <div className='absolute inset-0 bg-grid-dark fade-mask-radial' />
            <div className='absolute -top-48 left-1/2 h-[620px] w-[1100px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(99,102,241,0.38),transparent)]' />
            <div className='absolute left-[-12%] top-[18%] size-[520px] animate-aurora rounded-full bg-emerald-500/[0.16] blur-[120px]' />
            <div className='absolute right-[-10%] top-[6%] size-[560px] animate-aurora-slow rounded-full bg-rose-600/[0.18] blur-[130px]' />
            <div className='absolute left-[30%] top-[46%] size-[420px] animate-aurora rounded-full bg-sky-500/[0.12] blur-[120px]' />
            <div className='absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent' />
        </div>
    )
}

export const Hero: FC<HeroProps> = ({ viewer, onOpenLogin }): ReactNode => {
    return (
        <section id='top' aria-labelledby='hero-title' className='relative z-10 px-5 pt-32 sm:pt-40'>
            <div className='mx-auto max-w-5xl text-center'>
                <motion.div {...reveal(0)} className='inline-flex items-center gap-2.5 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-2 text-[10px] font-bold tracking-[0.16em] text-slate-200 backdrop-blur sm:rounded-full sm:text-[11px]'>
                    <span className='relative flex size-2 shrink-0'>
                        <span className='absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75' />
                        <span className='relative inline-flex size-2 rounded-full bg-emerald-400' />
                    </span>

                    <span className='hidden sm:inline'>GURUKULA KANGRI UNIVERSITY · B.TECH (1ST TO 4TH YEAR) ECOSYSTEM</span>
                    <span className='sm:hidden'>GKV · B.TECH 1ST TO 4TH YEAR ECOSYSTEM</span>
                </motion.div>

                <h1 id='hero-title' className='mt-7 text-balance text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-6xl lg:text-7xl'>
                    {LINES.map((line, lineIndex) => (
                        <span key={line.join(' ')} className='sm:block'>
                            {line.map((word, index) => (
                                <motion.span key={word} {...reveal(0.17 + (lineIndex * line.length + index) * 0.07)} className='mr-[0.22em] inline-block'>
                                    {word}
                                </motion.span>
                            ))}
                        </span>
                    ))}

                    <motion.span {...reveal(0.62)} className='inline-block bg-gradient-to-r from-indigo-300 via-sky-200 to-emerald-300 bg-clip-text pb-2 pr-2 font-serif text-[1.1em] font-normal italic tracking-normal text-transparent'>
                        At Every Step
                    </motion.span>
                </h1>

                <motion.p {...reveal(0.78)} className='mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg'>
                    From 1st-Year Foundation to 4th-Year Campus Placements, one unified platform for GKV B.Tech students (CSE, ECE, EE, Mechanical), Club Coordinators, and the T&P Cell.
                </motion.p>

                <motion.div {...reveal(0.9)} className='mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row'>
                    <motion.a
                        href='#pricing'
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        className='group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-crimson px-7 py-4 text-[15px] font-semibold text-white shadow-[0_18px_50px_-12px_rgba(217,45,32,0.85)] transition-colors hover:bg-crimson-dark sm:w-auto'
                    >
                        <span aria-hidden='true' className='absolute inset-y-0 left-0 w-1/3 animate-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent' />

                        <span className='relative'>Get Student Pass & Register</span>

                        <ArrowRight className='relative size-4 transition-transform group-hover:translate-x-1' />
                    </motion.a>

                    {viewer ? (
                        <Link href='/portal' className='inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.06] px-7 py-4 text-[15px] font-semibold text-white backdrop-blur transition hover:bg-white/[0.12] sm:w-auto'>
                            Open your portal

                            <ArrowRight className='size-4' />
                        </Link>
                    ) : (
                        <motion.button
                            type='button'
                            onClick={() => onOpenLogin('st')}
                            whileHover={{ y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            className='inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.06] px-7 py-4 text-[15px] font-semibold text-white backdrop-blur transition-colors hover:bg-white/[0.12] sm:w-auto'
                        >
                            Already Registered? Sign In

                            <ArrowRight className='size-4' />
                        </motion.button>
                    )}
                </motion.div>

                <motion.ul {...reveal(1.02)} className='mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px] font-medium text-slate-400'>
                    {["All 4 FET departments", "32-month skill roadmap", "Semester 1–8 Study Vault"].map(item => (
                        <li key={item} className='flex items-center gap-1.5'>
                            <span className='grid size-4 place-items-center rounded-full bg-emerald-500/15 text-emerald-400'>
                                <Check className='size-3' strokeWidth={3} />
                            </span>

                            {item}
                        </li>
                    ))}
                </motion.ul>
            </div>
        </section>
    )
}
